import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatError, photoUrl as resolvePhotoUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { JHARKHAND_DISTRICTS, RELIGIONS, MARITAL, DIET, EDUCATION, RASHIS, NAKSHATRAS, blocksFor, JHARKHAND_CASTES, OCCUPATIONS } from "@/data/jharkhand";
import { PasswordInput } from "@/components/ui/password-input";
import { 
  Camera, X, Crown, Save, BadgeCheck, ShieldCheck, 
  User, MapPin, Briefcase, Users, Sparkles, Heart, Check, Loader2, FileText, Wand2, Eye, EyeOff
} from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { useLanguage } from "@/context/LanguageContext";

const steps = [
  { id: "personal", label: "Personal Details", icon: User },
  { id: "location", label: "Location & Address", icon: MapPin },
  { id: "education", label: "Education & Job", icon: Briefcase },
  { id: "family", label: "Family Info", icon: Users },
  { id: "kundli", label: "Kundli", icon: Sparkles },
  { id: "partner", label: "Partner Prefs", icon: Heart },
  { id: "photos", label: "Photos", icon: Camera },
  { id: "account", label: "Account Settings", icon: ShieldCheck },
];

export default function Profile() {
  const { user, refresh } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [f, setF] = useState(null);
  const [currentStep, setCurrentStep] = useState("personal");
  const [photoUrl, setPhotoUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draftStatus, setDraftStatus] = useState("saved"); // 'saving' | 'saved' | 'error'
  
  // AI Bio generation state
  const [bioModalOpen, setBioModalOpen] = useState(false);
  const [bioLoading, setBioLoading] = useState(false);
  const [bioSuggestions, setBioSuggestions] = useState([]);
  const [selectedBioIdx, setSelectedBioIdx] = useState(null);

  // Timer reference for auto-save debounce
  const saveTimeoutRef = useRef(null);

  // Account Security states (Email, Phone & Password Change with OTP)
  const [emailStep, setEmailStep] = useState(1); // 1: request, 2: verify
  const [emailForm, setEmailForm] = useState({ current_password: "", new_email: "", otp: "" });
  const [updatingEmail, setUpdatingEmail] = useState(false);

  const [phoneStep, setPhoneStep] = useState(1); // 1: request, 2: verify
  const [phoneForm, setPhoneForm] = useState({ current_password: "", new_phone: "", otp: "" });
  const [updatingPhone, setUpdatingPhone] = useState(false);

  const [passForm, setPassForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [updatingPass, setUpdatingPass] = useState(false);

  // ── EMAIL CHANGE WITH OTP ──
  const handleRequestEmailOtp = async (e) => {
    e.preventDefault();
    if (!emailForm.current_password) return toast.error("Please enter your current password");
    if (!emailForm.new_email || !emailForm.new_email.includes("@")) return toast.error("Please enter a valid email address");
    
    setUpdatingEmail(true);
    try {
      const { data } = await api.post("/auth/request-email-change", {
        current_password: emailForm.current_password,
        new_email: emailForm.new_email,
      });
      toast.success(data.message || "OTP sent! Please check your new email inbox.");
      setEmailStep(2);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setUpdatingEmail(false);
    }
  };

  const handleVerifyEmailOtp = async (e) => {
    e.preventDefault();
    if (!emailForm.otp || emailForm.otp.trim().length < 4) return toast.error("Please enter the 6-digit OTP code");
    
    setUpdatingEmail(true);
    try {
      const { data } = await api.post("/auth/verify-email-change", {
        new_email: emailForm.new_email,
        otp: emailForm.otp,
      });
      toast.success(data.message || "Email address verified & updated successfully!");
      setEmailForm({ current_password: "", new_email: "", otp: "" });
      setEmailStep(1);
      await refresh();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setUpdatingEmail(false);
    }
  };

  // ── PHONE CHANGE WITH OTP ──
  const handleRequestPhoneOtp = async (e) => {
    e.preventDefault();
    if (!phoneForm.current_password) return toast.error("Please enter your current password");
    if (!phoneForm.new_phone || phoneForm.new_phone.trim().length < 10) return toast.error("Please enter a valid 10-digit mobile number");
    
    setUpdatingPhone(true);
    try {
      const { data } = await api.post("/auth/request-phone-change", {
        current_password: phoneForm.current_password,
        new_phone: phoneForm.new_phone,
      });
      toast.success(data.message || "OTP sent! Please enter the code sent to your new phone.");
      setPhoneStep(2);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setUpdatingPhone(false);
    }
  };

  const handleVerifyPhoneOtp = async (e) => {
    e.preventDefault();
    if (!phoneForm.otp || phoneForm.otp.trim().length < 4) return toast.error("Please enter the 6-digit OTP code");
    
    setUpdatingPhone(true);
    try {
      const { data } = await api.post("/auth/verify-phone-change", {
        new_phone: phoneForm.new_phone,
        otp: phoneForm.otp,
      });
      toast.success(data.message || "Phone number verified & updated successfully!");
      setPhoneForm({ current_password: "", new_phone: "", otp: "" });
      setPhoneStep(1);
      await refresh();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setUpdatingPhone(false);
    }
  };

  // ── PASSWORD CHANGE ──
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (!passForm.current_password) return toast.error("Please enter your current password");
    if (!passForm.new_password || passForm.new_password.length < 6) return toast.error("New password must be at least 6 characters long");
    if (passForm.new_password !== passForm.confirm_password) return toast.error("New passwords do not match!");

    setUpdatingPass(true);
    try {
      const { data } = await api.put("/auth/change-password", {
        current_password: passForm.current_password,
        new_password: passForm.new_password,
      });
      toast.success(data.message || "Password updated successfully!");
      setPassForm({ current_password: "", new_password: "", confirm_password: "" });
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setUpdatingPass(false);
    }
  };

  useEffect(() => {
    if (user) {
      // Ensure nested fields are initialized
      setF({
        ...user,
        partner_pref: user.partner_pref || {
          min_age: 18,
          max_age: 50,
          min_height: "none",
          max_height: "none",
          religion: "Any",
          caste: "Any",
          marital_status: "Any",
          education: "Any",
          occupation: "Any",
          income: "Any",
          manglik: "Any",
          mother_tongue: "Any",
          district: "Any",
          special_case: "No",
          profile_managed_by: "Any",
        }
      });
    }
  }, [user]);

  // Handle auto-save PUT request
  const saveDraft = useCallback(async (dataToSave) => {
    if (!dataToSave) return false;

    // Validate DOB age before saving
    if (dataToSave.dob) {
      const dobDate = new Date(dataToSave.dob);
      const today = new Date();
      let age = today.getFullYear() - dobDate.getFullYear();
      const m = today.getMonth() - dobDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < dobDate.getDate())) {
        age--;
      }
      const gender = (dataToSave.gender || user?.gender || "").toLowerCase();
      const isMale = gender.startsWith("m");
      const requiredAge = isMale ? 21 : 18;
      if (age < requiredAge) {
        setDraftStatus("error");
        toast.error("Under Indian Matrimonial Law, the minimum age for a groom (male) is 21 years / bride (female) is 18 years.");
        return false;
      }
    }

    setDraftStatus("saving");
    setSaving(true);
    try {
      const payload = { 
        ...dataToSave, 
        height_cm: dataToSave.height_cm ? Number(dataToSave.height_cm) : undefined, 
        siblings: dataToSave.siblings !== undefined && dataToSave.siblings !== "" ? Number(dataToSave.siblings) : undefined 
      };
      
      // Clean up server-managed fields
      delete payload.password_hash; 
      delete payload.role; 
      delete payload.email; 
      delete payload.user_id;
      delete payload.is_premium;
      delete payload.premium_expires_at;
      delete payload.premium_plan;
      delete payload.referral_code;
      delete payload.referred_by;
      delete payload.referral_count;
      delete payload.created_at;
      delete payload.updated_at;

      await api.put("/profile", payload);
      await refresh();
      setDraftStatus("saved");
      setSaving(false);
      return true;
    } catch (e) {
      console.error(e);
      setDraftStatus("error");
      setSaving(false);
      toast.error(formatError(e));
      return false;
    }
  }, [refresh, user]);

  // Trigger save on state change with a debounce
  const updateField = (key, val) => {
    setF((prev) => {
      const updated = { ...prev, [key]: val };
      
      // Debounce the save request
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = setTimeout(() => {
        saveDraft(updated);
      }, 1000); // 1-second debounce

      return updated;
    });
  };

  const updateNestedField = (parentKey, key, val) => {
    setF((prev) => {
      const updated = {
        ...prev,
        [parentKey]: {
          ...(prev[parentKey] || {}),
          [key]: val
        }
      };

      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = setTimeout(() => {
        saveDraft(updated);
      }, 1000);

      return updated;
    });
  };

  if (!f) return null;

  const addPhoto = () => {
    if (photoUrl) {
      const updatedPhotos = [...(f.photos || []), photoUrl];
      setF(prev => ({ ...prev, photos: updatedPhotos }));
      saveDraft({ ...f, photos: updatedPhotos });
      setPhotoUrl("");
      toast.success("Photo added successfully!");
    }
  };

  const rmPhoto = (i) => {
    const updatedPhotos = f.photos.filter((_, x) => x !== i);
    setF(prev => ({ ...prev, photos: updatedPhotos }));
    saveDraft({ ...f, photos: updatedPhotos });
    toast.success("Photo removed");
  };

  const uploadFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/photo", fd, { 
        headers: { "Content-Type": "multipart/form-data" } 
      });
      const updatedPhotos = [...(f.photos || []), data.url];
      setF(prev => ({ ...prev, photos: updatedPhotos }));
      saveDraft({ ...f, photos: updatedPhotos });
      toast.success("Photo uploaded successfully!");
    } catch (e) { 
      toast.error(formatError(e)); 
    } finally { 
      setUploading(false); 
    }
  };

  // Navigating steps
  const navigateStep = (direction) => {
    const currentIndex = steps.findIndex(s => s.id === currentStep);
    let nextIndex = currentIndex;
    
    if (direction === "next" && currentIndex < steps.length - 1) {
      nextIndex = currentIndex + 1;
    } else if (direction === "prev" && currentIndex > 0) {
      nextIndex = currentIndex - 1;
    }
    
    // Save draft immediately on step switch
    saveDraft(f);
    setCurrentStep(steps[nextIndex].id);
  };

  // Save current step and optionally navigate
  const saveStep = async (goNext = false, goBack = false, goHome = false) => {
    const success = await saveDraft(f);
    if (success) {
      toast.success("Changes saved!");
      if (goHome) navigate("/app");
      else if (goNext) navigateStep("next");
      else if (goBack) navigateStep("prev");
    }
  };

  const currentStepIndex = steps.findIndex(s => s.id === currentStep);
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === steps.length - 1;

  return (
    <div className="container-px py-8 max-w-5xl mx-auto">
      
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <span className="label-cap">My Profile Settings</span>
          <h1 className="font-heading text-4xl text-primary mt-1">{f.name || "Edit Profile"}</h1>
          <p className="text-xs text-muted-foreground mt-1">{user?.email}</p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <Button variant="outline" asChild className="rounded-full" data-testid="profile-shortlist-link">
            <Link to="/app/shortlist">My Shortlist</Link>
          </Button>
          <Button variant="outline" asChild className="rounded-full gap-1.5" data-testid="profile-biodata-link">
            <Link to="/app/biodata"><FileText className="w-4 h-4" />Bio-Data</Link>
          </Button>
          <Button variant="outline" asChild className="rounded-full gap-1.5 text-primary border-primary/30 hover:bg-primary/5" data-testid="profile-preview-btn">
            <Link to={`/app/profile/${f.user_id}`} target="_blank">
              <Eye className="w-4 h-4" />
              {language === "hi" ? "पूर्वावलोकन" : "Preview"}
            </Link>
          </Button>
          {!f.is_premium && (
            <Button asChild className="rounded-full bg-secondary hover:bg-secondary/90 text-secondary-foreground" data-testid="profile-premium-link">
              <Link to="/app/premium"><Crown className="w-4 h-4 mr-1.5"/>Premium</Link>
            </Button>
          )}
          {/* Close / Back to Home button */}
          <button
            onClick={() => navigate("/app")}
            title="Close & go to home"
            className="ml-1 w-9 h-9 rounded-full border border-border hover:bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
            data-testid="profile-close-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="grid lg:grid-cols-4 gap-6">
        
        {/* Left Side Tab Navigation */}
        <div className="lg:col-span-1 space-y-4">
          
          {/* Trust Badge */}
          {f.is_verified ? (
            <div className="card-soft p-4 flex items-center gap-3 border-emerald-500/20 bg-emerald-500/5" data-testid="profile-verified-card">
              <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center shrink-0">
                <BadgeCheck className="w-5 h-5 text-emerald-600" />
              </div>
              <div className="min-w-0">
                <div className="font-medium text-foreground text-sm">Verified Profile</div>
                <div className="text-[10px] text-muted-foreground">ID Verification trust badge active</div>
              </div>
            </div>
          ) : (
            <Link to="/app/verify" className="card-soft p-4 flex items-center gap-3 hover:border-primary/40 transition-colors bg-accent/20" data-testid="profile-verify-cta">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <ShieldCheck className="w-5 h-5 text-primary" />
              </div>
              <div className="min-w-0">
                <div className="font-medium text-foreground text-sm">Verify ID Badge ✓</div>
                <div className="text-[10px] text-muted-foreground">Earn a trust badge now.</div>
              </div>
            </Link>
          )}

          {/* Stepper Tabs Menu */}
          <div className="card-soft p-3 space-y-1">
            {steps.map((step) => {
              const Icon = step.icon;
              const active = currentStep === step.id;
              return (
                <button
                  key={step.id}
                  onClick={() => { saveDraft(f); setCurrentStep(step.id); }}
                  className={`w-full text-left px-4 py-2.5 rounded-xl text-sm flex items-center gap-2.5 transition-colors font-medium ${
                    active 
                      ? "bg-primary text-primary-foreground shadow-sm" 
                      : "text-foreground/80 hover:bg-muted"
                  }`}
                  data-testid={`step-tab-${step.id}`}
                >
                  <Icon className={`w-4 h-4 ${active ? "text-primary-foreground" : "text-secondary"}`} />
                  {step.label}
                </button>
              );
            })}
          </div>

          {/* Draft Saved Status indicator */}
          <div className="flex items-center justify-center gap-1.5 py-2 px-4 card-soft bg-card text-xs">
            {draftStatus === "saving" && (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin text-secondary" />
                <span className="text-muted-foreground font-medium">Saving draft changes...</span>
              </>
            )}
            {draftStatus === "saved" && (
              <>
                <Check className="w-3.5 h-3.5 text-emerald-500" strokeWidth={3} />
                <span className="text-muted-foreground font-medium">Draft saved automatically</span>
              </>
            )}
            {draftStatus === "error" && (
              <>
                <span className="w-2.5 h-2.5 rounded-full bg-red-500 shrink-0" />
                <span className="text-red-500 font-semibold">Error saving changes</span>
              </>
            )}
          </div>

        </div>

        {/* Right Side wizard form container */}
        <div className="lg:col-span-3 card-soft p-6 flex flex-col justify-between min-h-[500px]">
          
          {/* Step Contents */}
          <div className="flex-1">
            
            {/* Step 1: Personal Details */}
            {currentStep === "personal" && (
              <div className="space-y-5 animate-in fade-in duration-300">
                <h3 className="font-heading text-2xl text-primary border-b border-border pb-2">Personal Details</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <PF label="Name">
                    <Input value={f.name || ""} onChange={(e) => updateField("name", e.target.value)} data-testid="pf-name"/>
                  </PF>
                  <PF label="Gender">
                    <Select value={f.gender || ""} onValueChange={(v) => updateField("gender", v)}>
                      <SelectTrigger data-testid="pf-gender"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                      </SelectContent>
                    </Select>
                  </PF>
                  <PF label="Date of birth">
                    <Input type="date" value={f.dob || ""} onChange={(e) => updateField("dob", e.target.value)} data-testid="pf-dob"/>
                  </PF>
                  <PF label="Height (cm)">
                    <Input type="number" value={f.height_cm || ""} onChange={(e) => updateField("height_cm", e.target.value)} data-testid="pf-height"/>
                  </PF>
                  <PF label="Marital status">
                    <PSel value={f.marital_status} onChange={(v) => updateField("marital_status", v)} items={MARITAL} testid="pf-marital"/>
                  </PF>
                  <PF label="Diet preference">
                    <PSel value={f.diet} onChange={(v) => updateField("diet", v)} items={DIET} testid="pf-diet"/>
                  </PF>
                  <PF label="Profile Managed By">
                    <PSel value={f.profile_managed_by} onChange={(v) => updateField("profile_managed_by", v)} items={["Self", "Parent", "Sibling", "Relative", "Friend"]} testid="pf-managedby"/>
                  </PF>
                  <PF label="About Myself" className="sm:col-span-2">
                    <div className="space-y-2">
                      <Textarea value={f.about || ""} onChange={(e) => updateField("about", e.target.value)} rows={4} data-testid="pf-about" placeholder="Write something about yourself — your personality, values, interests, and what you're looking for..."/>
                      <button
                        type="button"
                        data-testid="btn-generate-bio"
                        onClick={async () => {
                          setBioModalOpen(true);
                          setBioLoading(true);
                          setBioSuggestions([]);
                          setSelectedBioIdx(null);
                          try {
                            const { data } = await api.post("/profile/generate-bio", {
                              name: f.name, gender: f.gender, dob: f.dob,
                              height_cm: f.height_cm, marital_status: f.marital_status,
                              diet: f.diet, education: f.education, occupation: f.occupation,
                              district: f.district, block: f.block,
                              religion: f.religion, caste: f.caste,
                            });
                            setBioSuggestions(data.suggestions || []);
                          } catch (e) {
                            toast.error("Could not generate bio suggestions. Please try again.");
                            setBioModalOpen(false);
                          } finally {
                            setBioLoading(false);
                          }
                        }}
                        className="inline-flex items-center gap-2 text-sm font-medium px-3 py-1.5 rounded-full border border-[#6B1A24]/30 text-[#6B1A24] bg-[#FFF0F2] hover:bg-[#6B1A24] hover:text-white transition-all duration-200"
                      >
                        <Wand2 className="w-3.5 h-3.5" />
                        Generate with AI
                      </button>
                    </div>
                  </PF>
                </div>
              </div>
            )}

            {/* Step 2: Location & Address */}
            {currentStep === "location" && (
              <div className="space-y-5 animate-in fade-in duration-300">
                <h3 className="font-heading text-2xl text-primary border-b border-border pb-2">Location & address</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <PF label="District">
                    <PSel value={f.district} onChange={(v) => setF((d) => ({ ...d, district: v, block: "" }))} items={JHARKHAND_DISTRICTS} testid="pf-district"/>
                  </PF>
                  <PF label="Block / Tehsil">
                    <Select value={f.block || ""} onValueChange={(v) => updateField("block", v)}>
                      <SelectTrigger data-testid="pf-block"><SelectValue placeholder="Select block" /></SelectTrigger>
                      <SelectContent>
                        {blocksFor(f.district).map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </PF>
                  <PF label="Village / Locality">
                    <Input value={f.village || ""} onChange={(e) => updateField("village", e.target.value)} data-testid="pf-village"/>
                  </PF>
                  <PF label="City">
                    <Input value={f.city || ""} onChange={(e) => updateField("city", e.target.value)} data-testid="pf-city"/>
                  </PF>
                </div>
              </div>
            )}

            {/* Step 3: Education & Job */}
            {currentStep === "education" && (
              <div className="space-y-5 animate-in fade-in duration-300">
                <h3 className="font-heading text-2xl text-primary border-b border-border pb-2">Education & Job details</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <PF label="Education Qualification">
                    <PSel value={f.education} onChange={(v) => updateField("education", v)} items={EDUCATION} testid="pf-education"/>
                  </PF>
                  <PF label="Occupation / Job title">
                    <ProfileComboSelect
                      value={f.occupation || ""}
                      onChange={(v) => updateField("occupation", v)}
                      options={OCCUPATIONS}
                      placeholder="Search or type occupation…"
                      testid="pf-occupation"
                    />
                  </PF>
                  <PF label="Annual Income range">
                    <Input value={f.income || ""} onChange={(e) => updateField("income", e.target.value)} placeholder="e.g. ₹5,00,000 - ₹8,00,000" data-testid="pf-income"/>
                  </PF>
                </div>
              </div>
            )}

            {/* Step 4: Family Details */}
            {currentStep === "family" && (
              <div className="space-y-5 animate-in fade-in duration-300">
                <h3 className="font-heading text-2xl text-primary border-b border-border pb-2">Family Information</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <PF label="Family Type">
                    <Select value={f.family_type || ""} onValueChange={(v) => updateField("family_type", v)}>
                      <SelectTrigger data-testid="pf-family-type"><SelectValue placeholder="Select family type" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Joint">Joint Family</SelectItem>
                        <SelectItem value="Nuclear">Nuclear Family</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </PF>
                  <PF label="Father's Occupation">
                    <Input value={f.father_occupation || ""} onChange={(e) => updateField("father_occupation", e.target.value)} placeholder="e.g. Retired / Farmer" data-testid="pf-father-job"/>
                  </PF>
                  <PF label="Mother's Occupation">
                    <Input value={f.mother_occupation || ""} onChange={(e) => updateField("mother_occupation", e.target.value)} placeholder="e.g. Homemaker" data-testid="pf-mother-job"/>
                  </PF>
                  <PF label="Number of Siblings">
                    <Input type="number" value={f.siblings === undefined ? "" : f.siblings} onChange={(e) => updateField("siblings", e.target.value)} data-testid="pf-siblings"/>
                  </PF>
                </div>
              </div>
            )}

            {/* Step 5: Kundli & Birth details */}
            {currentStep === "kundli" && (
              <div className="space-y-5 animate-in fade-in duration-300">
                <h3 className="font-heading text-2xl text-primary border-b border-border pb-2">Kundli & Birth Details</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <PF label="Rashi">
                    <PSel value={f.rashi} onChange={(v) => updateField("rashi", v)} items={RASHIS} testid="pf-rashi"/>
                  </PF>
                  <PF label="Nakshatra">
                    <PSel value={f.nakshatra} onChange={(v) => updateField("nakshatra", v)} items={NAKSHATRAS} testid="pf-nakshatra"/>
                  </PF>
                  <PF label="Manglik Status">
                    <Select value={f.manglik || "No"} onValueChange={(v) => updateField("manglik", v)}>
                      <SelectTrigger data-testid="pf-manglik"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="No">Non-Manglik</SelectItem>
                        <SelectItem value="Yes">Manglik</SelectItem>
                        <SelectItem value="Anshik">Anshik / Partial Manglik</SelectItem>
                        <SelectItem value="Don't Know">Don't Know</SelectItem>
                      </SelectContent>
                    </Select>
                  </PF>
                  <PF label="Religion">
                    <PSel value={f.religion} onChange={(v) => updateField("religion", v)} items={RELIGIONS} testid="pf-religion"/>
                  </PF>
                  <PF label="Caste">
                    <ProfileComboSelect
                      value={f.caste || ""}
                      onChange={(v) => updateField("caste", v)}
                      options={JHARKHAND_CASTES}
                      placeholder="Search or type caste…"
                      testid="pf-caste"
                    />
                  </PF>
                  <PF label="Birth Time">
                    <Input type="time" value={f.birth_time || ""} onChange={(e) => updateField("birth_time", e.target.value)} data-testid="pf-birth-time"/>
                  </PF>
                  <PF label="Birth Place">
                    <Input value={f.birth_place || ""} onChange={(e) => updateField("birth_place", e.target.value)} placeholder="e.g. Ranchi" data-testid="pf-birth-place"/>
                  </PF>
                </div>
              </div>
            )}

            {/* Step 6: Partner Preferences */}
            {currentStep === "partner" && (
              <div className="space-y-6 animate-in fade-in duration-300">
                <h3 className="font-heading text-2xl text-primary border-b border-border pb-2">Partner Preferences</h3>

                {/* ── Basic Details ── */}
                <div>
                  <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2"><span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-[10px] flex items-center justify-center font-bold">1</span>Basic Details</h4>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <PF label="Minimum Age">
                      <Input type="number" min={18} max={70}
                        value={f.partner_pref?.min_age || 18}
                        onChange={(e) => updateNestedField("partner_pref", "min_age", Number(e.target.value))}
                        data-testid="pref-min-age" />
                    </PF>
                    <PF label="Maximum Age">
                      <Input type="number" min={18} max={70}
                        value={f.partner_pref?.max_age || 50}
                        onChange={(e) => updateNestedField("partner_pref", "max_age", Number(e.target.value))}
                        data-testid="pref-max-age" />
                    </PF>
                    <PF label="Minimum Height">
                      <Select value={f.partner_pref?.min_height || "none"} onValueChange={(v) => updateNestedField("partner_pref", "min_height", v)}>
                        <SelectTrigger data-testid="pref-min-height"><SelectValue placeholder="No preference" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No preference</SelectItem>
                          {["4'6\"","4'8\"","4'10\"","5'0\"","5'1\"","5'2\"","5'3\"","5'4\"","5'5\"","5'6\"","5'7\"","5'8\"","5'9\"","5'10\"","5'11\"","6'0\"","6'1\"","6'2\""].map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </PF>
                    <PF label="Maximum Height">
                      <Select value={f.partner_pref?.max_height || "none"} onValueChange={(v) => updateNestedField("partner_pref", "max_height", v)}>
                        <SelectTrigger data-testid="pref-max-height"><SelectValue placeholder="No preference" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No preference</SelectItem>
                          {["4'6\"","4'8\"","4'10\"","5'0\"","5'1\"","5'2\"","5'3\"","5'4\"","5'5\"","5'6\"","5'7\"","5'8\"","5'9\"","5'10\"","5'11\"","6'0\"","6'1\"","6'2\""].map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </PF>
                    <PF label="Marital Status Preference">
                      <Select value={f.partner_pref?.marital_status || "Any"} onValueChange={(v) => updateNestedField("partner_pref", "marital_status", v)}>
                        <SelectTrigger data-testid="pref-marital"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Any">Any</SelectItem>
                          {MARITAL.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </PF>
                    <PF label="Preferred District / Location">
                      <Select value={f.partner_pref?.district || "Any"} onValueChange={(v) => updateNestedField("partner_pref", "district", v)}>
                        <SelectTrigger data-testid="pref-district"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Any">Any Location</SelectItem>
                          {JHARKHAND_DISTRICTS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </PF>
                    <PF label="Profile Managed By">
                      <Select value={f.partner_pref?.profile_managed_by || "Any"} onValueChange={(v) => updateNestedField("partner_pref", "profile_managed_by", v)}>
                        <SelectTrigger data-testid="pref-managed-by"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Any">Any</SelectItem>
                          <SelectItem value="Self">Self</SelectItem>
                          <SelectItem value="Parents">Parents</SelectItem>
                          <SelectItem value="Siblings">Siblings</SelectItem>
                          <SelectItem value="Relatives">Relatives</SelectItem>
                          <SelectItem value="Friends">Friends</SelectItem>
                        </SelectContent>
                      </Select>
                    </PF>
                    <PF label="Special Case">
                      <Select value={f.partner_pref?.special_case || "No"} onValueChange={(v) => updateNestedField("partner_pref", "special_case", v)}>
                        <SelectTrigger data-testid="pref-special-case"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="No">No Preference</SelectItem>
                          <SelectItem value="Differently Abled">Differently Abled Accepted</SelectItem>
                          <SelectItem value="Single Parent">Single Parent Accepted</SelectItem>
                        </SelectContent>
                      </Select>
                    </PF>
                  </div>
                </div>

                {/* ── Education & Occupation ── */}
                <div>
                  <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2"><span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-[10px] flex items-center justify-center font-bold">2</span>Education &amp; Occupation</h4>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <PF label="Preferred Education">
                      <Select value={f.partner_pref?.education || "Any"} onValueChange={(v) => updateNestedField("partner_pref", "education", v)}>
                        <SelectTrigger data-testid="pref-education"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Any">Any Education</SelectItem>
                          {EDUCATION.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </PF>
                    <PF label="Preferred Occupation">
                      <Select value={f.partner_pref?.occupation || "Any"} onValueChange={(v) => updateNestedField("partner_pref", "occupation", v)}>
                        <SelectTrigger data-testid="pref-occupation"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Any">Any Occupation</SelectItem>
                          {OCCUPATIONS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </PF>
                    <PF label="Annual Income Preference">
                      <Select value={f.partner_pref?.income || "Any"} onValueChange={(v) => updateNestedField("partner_pref", "income", v)}>
                        <SelectTrigger data-testid="pref-income"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Any">No Preference</SelectItem>
                          <SelectItem value="0-2 LPA">0–2 LPA</SelectItem>
                          <SelectItem value="2-5 LPA">2–5 LPA</SelectItem>
                          <SelectItem value="5-10 LPA">5–10 LPA</SelectItem>
                          <SelectItem value="10-20 LPA">10–20 LPA</SelectItem>
                          <SelectItem value="20+ LPA">20+ LPA</SelectItem>
                        </SelectContent>
                      </Select>
                    </PF>
                  </div>
                </div>

                {/* ── Religion & Ethnicity ── */}
                <div>
                  <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2"><span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-[10px] flex items-center justify-center font-bold">3</span>Religion &amp; Ethnicity</h4>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <PF label="Preferred Religion">
                      <Select value={f.partner_pref?.religion || "Any"} onValueChange={(v) => updateNestedField("partner_pref", "religion", v)}>
                        <SelectTrigger data-testid="pref-religion"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Any">Any Religion</SelectItem>
                          {RELIGIONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </PF>
                    <PF label="Preferred Caste">
                      <Select value={f.partner_pref?.caste || "Any"} onValueChange={(v) => updateNestedField("partner_pref", "caste", v)}>
                        <SelectTrigger data-testid="pref-caste"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Any">Any Caste</SelectItem>
                          {JHARKHAND_CASTES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </PF>
                    <PF label="Mother Tongue Preference">
                      <Select value={f.partner_pref?.mother_tongue || "Any"} onValueChange={(v) => updateNestedField("partner_pref", "mother_tongue", v)}>
                        <SelectTrigger data-testid="pref-tongue"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Any">Any</SelectItem>
                          {["Hindi","Bhojpuri","Maithili","Nagpuri","Santali","Bengali","Urdu","Odia","Punjabi","English"].map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </PF>
                  </div>
                </div>

                {/* ── Lifestyle ── */}
                <div>
                  <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2"><span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-[10px] flex items-center justify-center font-bold">4</span>Lifestyle</h4>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <PF label="Manglik Preference">
                      <Select value={f.partner_pref?.manglik || "Any"} onValueChange={(v) => updateNestedField("partner_pref", "manglik", v)}>
                        <SelectTrigger data-testid="pref-manglik"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Any">Any (No preference)</SelectItem>
                          <SelectItem value="No">Should not be Manglik</SelectItem>
                          <SelectItem value="Yes">Should be Manglik</SelectItem>
                        </SelectContent>
                      </Select>
                    </PF>
                    <PF label="Diet Preference">
                      <Select value={f.partner_pref?.diet || "Any"} onValueChange={(v) => updateNestedField("partner_pref", "diet", v)}>
                        <SelectTrigger data-testid="pref-diet"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Any">Any</SelectItem>
                          {DIET.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </PF>
                  </div>
                </div>
              </div>
            )}

            {/* Step 7: Photos Upload & Links */}
            {currentStep === "photos" && (
              <div className="space-y-5 animate-in fade-in duration-300">
                <h3 className="font-heading text-2xl text-primary border-b border-border pb-2 font-semibold flex items-center justify-between">
                  Profile Photos
                  <span className="text-xs text-muted-foreground font-normal">At least 1 photo is required</span>
                </h3>
                
                {/* Upload Section */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {(f.photos || []).map((p, i) => (
                    <div key={i} className="relative aspect-square rounded-2xl overflow-hidden border border-border group shadow-sm bg-muted/30">
                      <img 
                        src={resolvePhotoUrl(p)} 
                        alt="Profile photo" 
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          // Show broken image placeholder
                          e.target.style.display = "none";
                          e.target.nextSibling.style.display = "flex";
                        }}
                      />
                      <div style={{display:"none"}} className="absolute inset-0 flex flex-col items-center justify-center bg-muted/60 text-xs text-muted-foreground text-center p-2">
                        <span className="text-2xl mb-1">⚠️</span>
                        Photo not found<br/>
                        <button onClick={() => rmPhoto(i)} className="mt-1 text-red-500 underline text-[10px]">Remove</button>
                      </div>
                      <button 
                        onClick={() => rmPhoto(i)} 
                        className="absolute top-2 right-2 bg-black/60 hover:bg-black/95 text-white rounded-full p-1.5 transition-colors" 
                        data-testid={`pf-rm-${i}`}
                      >
                        <X className="w-3.5 h-3.5"/>
                      </button>
                    </div>
                  ))}

                  {/* Upload box */}
                  <label className="border-2 border-dashed border-border hover:border-secondary rounded-2xl aspect-square flex flex-col items-center justify-center cursor-pointer transition-colors bg-muted/10 group">
                    <Camera className="w-8 h-8 text-muted-foreground group-hover:text-secondary transition-colors" strokeWidth={1.5} />
                    <span className="text-xs text-muted-foreground mt-2 font-medium">Upload File</span>
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={(e) => uploadFile(e.target.files?.[0])} 
                      className="hidden" 
                      disabled={uploading}
                    />
                  </label>
                </div>

                {/* Paste URL option */}
                <div className="pt-4 border-t border-border/60">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Or paste an Image URL</Label>
                  <div className="flex gap-2 mt-2 max-w-lg">
                    <Input 
                      value={photoUrl} 
                      onChange={(e) => setPhotoUrl(e.target.value)} 
                      placeholder="https://images.unsplash.com/photo-..." 
                      data-testid="pf-photo-url" 
                      className="rounded-full"
                    />
                    <Button 
                      type="button" 
                      onClick={addPhoto} 
                      className="rounded-full shrink-0 bg-secondary hover:bg-secondary/90 text-secondary-foreground" 
                      data-testid="pf-photo-add"
                    >
                      Add URL
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Step 8: Account Settings (Email, Phone & Password) */}
            {currentStep === "account" && (
              <div className="space-y-8 animate-in fade-in duration-300">
                <h3 className="font-heading text-2xl text-primary border-b border-border pb-2 font-semibold flex items-center justify-between">
                  <span>Account &amp; Security Settings</span>
                  <span className="text-xs text-muted-foreground font-normal">Manage Email, Phone &amp; Password</span>
                </h3>

                {/* Current Registered Info Banner */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="p-4 rounded-2xl bg-primary/5 border border-primary/20 flex items-center justify-between">
                    <div>
                      <span className="text-xs text-muted-foreground block font-medium uppercase tracking-wider">Registered Email</span>
                      <span className="font-bold text-sm text-foreground">{user?.email || "No email linked"}</span>
                    </div>
                    <BadgeCheck className="w-5 h-5 text-primary shrink-0" />
                  </div>
                  <div className="p-4 rounded-2xl bg-emerald-500/5 border border-emerald-500/20 flex items-center justify-between">
                    <div>
                      <span className="text-xs text-muted-foreground block font-medium uppercase tracking-wider">Registered Phone</span>
                      <span className="font-bold text-sm text-foreground">{user?.phone || "No phone linked"}</span>
                    </div>
                    <BadgeCheck className="w-5 h-5 text-emerald-600 shrink-0" />
                  </div>
                </div>

                {/* Section 1: Change Email Address (2-Step OTP) */}
                <div className="bg-white p-5 rounded-2xl border border-border/80 shadow-sm space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <User className="w-5 h-5 text-primary" />
                      <h4 className="font-bold text-base text-foreground">Change Email Address</h4>
                    </div>
                    {emailStep === 2 && (
                      <span className="text-xs font-semibold text-amber-800 bg-amber-100 px-2.5 py-0.5 rounded-full">Step 2: OTP Verification</span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    A 6-digit verification code (OTP) will be sent to your new email address.
                  </p>
                  
                  {emailStep === 1 ? (
                    <form onSubmit={handleRequestEmailOtp} className="space-y-4 pt-2">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <Label className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-1 block">New Email Address *</Label>
                          <Input 
                            type="email" 
                            placeholder="e.g. newemail@example.com" 
                            value={emailForm.new_email} 
                            onChange={(e) => setEmailForm({ ...emailForm, new_email: e.target.value })} 
                            className="bg-white"
                            data-testid="pf-new-email"
                          />
                        </div>
                        <div>
                          <Label className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-1 block">Current Password *</Label>
                          <PasswordInput 
                            placeholder="Enter current password" 
                            value={emailForm.current_password} 
                            onChange={(e) => setEmailForm({ ...emailForm, current_password: e.target.value })} 
                            testid="pf-email-pass"
                          />
                        </div>
                      </div>
                      <Button 
                        type="submit" 
                        disabled={updatingEmail} 
                        className="rounded-full bg-primary hover:bg-primary/90 text-white font-semibold text-xs px-6 py-2"
                        data-testid="pf-req-email-otp-btn"
                      >
                        {updatingEmail ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> Sending OTP...</> : "Send Verification OTP"}
                      </Button>
                    </form>
                  ) : (
                    <form onSubmit={handleVerifyEmailOtp} className="space-y-4 pt-2 animate-in fade-in">
                      <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl text-xs text-amber-900 font-medium">
                        📩 Verification code sent to <b>{emailForm.new_email}</b>. Check your inbox!
                      </div>
                      <div className="max-w-xs">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-1 block">Enter 6-Digit Email OTP *</Label>
                        <Input 
                          type="text" 
                          maxLength={6} 
                          placeholder="e.g. 481920" 
                          value={emailForm.otp} 
                          onChange={(e) => setEmailForm({ ...emailForm, otp: e.target.value })} 
                          className="bg-white text-center tracking-widest font-mono text-base h-11"
                          data-testid="pf-email-otp-input"
                        />
                      </div>
                      <div className="flex gap-3 items-center">
                        <Button 
                          type="submit" 
                          disabled={updatingEmail} 
                          className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-xs px-6 py-2"
                          data-testid="pf-verify-email-otp-btn"
                        >
                          {updatingEmail ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> Verifying...</> : "Verify & Change Email"}
                        </Button>
                        <Button 
                          type="button" 
                          variant="ghost" 
                          onClick={() => setEmailStep(1)} 
                          className="text-xs rounded-full"
                        >
                          Cancel / Edit Email
                        </Button>
                      </div>
                    </form>
                  )}
                </div>

                {/* Section 2: Change Phone Number (2-Step OTP) */}
                <div className="bg-white p-5 rounded-2xl border border-border/80 shadow-sm space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-emerald-600" />
                      <h4 className="font-bold text-base text-foreground">Change Phone Number</h4>
                    </div>
                    {phoneStep === 2 && (
                      <span className="text-xs font-semibold text-amber-800 bg-amber-100 px-2.5 py-0.5 rounded-full">Step 2: SMS Verification</span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    A 6-digit verification code (OTP) will be sent to your new mobile number via SMS.
                  </p>

                  {phoneStep === 1 ? (
                    <form onSubmit={handleRequestPhoneOtp} className="space-y-4 pt-2">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <Label className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-1 block">New Phone Number (10 Digits) *</Label>
                          <Input 
                            type="tel" 
                            placeholder="e.g. 9876543210" 
                            value={phoneForm.new_phone} 
                            onChange={(e) => setPhoneForm({ ...phoneForm, new_phone: e.target.value })} 
                            className="bg-white"
                            data-testid="pf-new-phone"
                          />
                        </div>
                        <div>
                          <Label className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-1 block">Current Password *</Label>
                          <PasswordInput 
                            placeholder="Enter current password" 
                            value={phoneForm.current_password} 
                            onChange={(e) => setPhoneForm({ ...phoneForm, current_password: e.target.value })} 
                            testid="pf-phone-pass"
                          />
                        </div>
                      </div>
                      <Button 
                        type="submit" 
                        disabled={updatingPhone} 
                        className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-xs px-6 py-2"
                        data-testid="pf-req-phone-otp-btn"
                      >
                        {updatingPhone ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> Sending SMS OTP...</> : "Send Verification SMS OTP"}
                      </Button>
                    </form>
                  ) : (
                    <form onSubmit={handleVerifyPhoneOtp} className="space-y-4 pt-2 animate-in fade-in">
                      <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl text-xs text-emerald-900 font-medium">
                        📱 Verification code sent to <b>{phoneForm.new_phone}</b>. Please enter it below!
                      </div>
                      <div className="max-w-xs">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-1 block">Enter 6-Digit Phone OTP *</Label>
                        <Input 
                          type="text" 
                          maxLength={6} 
                          placeholder="e.g. 918234" 
                          value={phoneForm.otp} 
                          onChange={(e) => setPhoneForm({ ...phoneForm, otp: e.target.value })} 
                          className="bg-white text-center tracking-widest font-mono text-base h-11"
                          data-testid="pf-phone-otp-input"
                        />
                      </div>
                      <div className="flex gap-3 items-center">
                        <Button 
                          type="submit" 
                          disabled={updatingPhone} 
                          className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-xs px-6 py-2"
                          data-testid="pf-verify-phone-otp-btn"
                        >
                          {updatingPhone ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> Verifying...</> : "Verify & Change Phone"}
                        </Button>
                        <Button 
                          type="button" 
                          variant="ghost" 
                          onClick={() => setPhoneStep(1)} 
                          className="text-xs rounded-full"
                        >
                          Cancel / Edit Number
                        </Button>
                      </div>
                    </form>
                  )}
                </div>

                {/* Section 3: Change Password */}
                <div className="bg-white p-5 rounded-2xl border border-border/80 shadow-sm space-y-4">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="w-5 h-5 text-amber-600" />
                    <h4 className="font-bold text-base text-foreground">Change Account Password</h4>
                  </div>
                  <p className="text-xs text-muted-foreground">Choose a strong password containing at least 6 characters.</p>

                  <form onSubmit={handlePasswordChange} className="space-y-4 pt-2">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div>
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-1 block">Current Password *</Label>
                        <PasswordInput 
                          placeholder="Current password" 
                          value={passForm.current_password} 
                          onChange={(e) => setPassForm({ ...passForm, current_password: e.target.value })} 
                          testid="pf-curr-pass"
                        />
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-1 block">New Password *</Label>
                        <PasswordInput 
                          placeholder="Min 6 characters" 
                          value={passForm.new_password} 
                          onChange={(e) => setPassForm({ ...passForm, new_password: e.target.value })} 
                          testid="pf-new-pass"
                        />
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-1 block">Confirm New Password *</Label>
                        <PasswordInput 
                          placeholder="Confirm new password" 
                          value={passForm.confirm_password} 
                          onChange={(e) => setPassForm({ ...passForm, confirm_password: e.target.value })} 
                          testid="pf-conf-pass"
                        />
                      </div>
                    </div>
                    <Button 
                      type="submit" 
                      disabled={updatingPass} 
                      className="rounded-full bg-amber-600 hover:bg-amber-700 text-white font-semibold text-xs px-6 py-2"
                      data-testid="pf-update-pass-btn"
                    >
                      {updatingPass ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> Updating Password...</> : "Update Password"}
                    </Button>
                  </form>
                </div>

                {/* Section 4: Profile Visibility */}
                <div className="bg-white p-5 rounded-2xl border border-border/80 shadow-sm space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {f.hidden ? <EyeOff className="w-5 h-5 text-red-500" /> : <Eye className="w-5 h-5 text-emerald-600" />}
                      <h4 className="font-bold text-base text-foreground">
                        {language === "hi" ? "प्रोफ़ाइल दृश्यता (छुपाएं / दिखाएं)" : "Profile Visibility (Pause / Hide)"}
                      </h4>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {language === "hi" 
                      ? "जब आपकी प्रोफ़ाइल छिपी (hidden) होती है, तो यह खोज और मिलान (browse/search) में दिखाई नहीं देगी। जो लोग पहले से जुड़े हैं, वे अभी भी आपकी प्रोफ़ाइल देख सकते हैं।"
                      : "When your profile is paused/hidden, it will be excluded from all searches, matches, and directory browse queries. Users you already connected with can still view your details."}
                  </p>
                  <div className="flex items-center justify-between p-3.5 rounded-xl border border-border bg-muted/20 mt-2">
                    <div>
                      <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground block">
                        {language === "hi" ? "प्रोफ़ाइल छिपाएं" : "Hide Profile"}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {f.hidden 
                          ? (language === "hi" ? "वर्तमान स्थिति: छिपी हुई (अदृश्य)" : "Status: Paused / Hidden from matches") 
                          : (language === "hi" ? "वर्तमान स्थिति: सक्रिय (सभी को दृश्य)" : "Status: Active & Visible to everyone")}
                      </span>
                    </div>
                    <Switch 
                      checked={!!f.hidden} 
                      onCheckedChange={(val) => {
                        updateField("hidden", val);
                        toast.success(
                          language === "hi"
                            ? (val ? "प्रोफ़ाइल अब छिपी हुई है" : "प्रोफ़ाइल अब दृश्यमान है")
                            : (val ? "Profile is now hidden" : "Profile is now visible")
                        );
                      }} 
                    />
                  </div>
                </div>
              </div>
            )}

          </div>

          {/* Stepper Navigation Buttons */}
          <div className="mt-8 pt-5 border-t border-border/60">
            {/* Primary Save Button */}
            <Button
              onClick={async () => {
                const success = await saveDraft(f);
                if (success) toast.success("Changes saved successfully!");
              }}
              disabled={saving}
              className="w-full rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground h-12 text-base font-semibold mb-3 gap-2"
              data-testid="pf-save"
            >
              {saving ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</>
              ) : (
                <><Save className="w-4 h-4" /> Save Changes</>
              )}
            </Button>

            {/* Secondary navigation row */}
            <div className="flex items-center gap-2">
              {/* Back button */}
              <Button
                variant="outline"
                disabled={isFirstStep}
                onClick={() => { saveDraft(f); navigateStep("prev"); }}
                className="rounded-xl flex-1"
                data-testid="prev-step-btn"
              >
                ← Back
              </Button>

              {/* Next / Finish button */}
              {!isLastStep ? (
                <Button
                  onClick={() => { saveDraft(f); navigateStep("next"); }}
                  variant="outline"
                  className="rounded-xl flex-1 border-primary/40 text-primary hover:bg-primary/5"
                  data-testid="next-step-btn"
                >
                  Next Step →
                </Button>
              ) : (
                <Button
                  onClick={async () => {
                    const success = await saveDraft(f);
                    if (success) {
                      toast.success("Profile completed!");
                      navigate("/app");
                    }
                  }}
                  disabled={saving}
                  className="rounded-xl flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="pf-finish"
                >
                  <Check className="w-4 h-4 mr-1" /> Finish & Go Home
                </Button>
              )}

              {/* Close / X button */}
              <button
                onClick={() => navigate("/app")}
                title="Close without saving"
                className="w-11 h-11 rounded-xl border border-border hover:bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors shrink-0"
                data-testid="pf-close-footer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* AI Bio Generation Modal */}
      {bioModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="relative bg-white dark:bg-[#1a0a0d] rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden border border-border">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-gradient-to-r from-[#6B1A24] to-[#9B2335]">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
                  <Wand2 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-white font-heading text-lg font-semibold">Generate About Myself</h2>
                  <p className="text-white/70 text-xs">Choose a style that best suits you</p>
                </div>
              </div>
              <button onClick={() => setBioModalOpen(false)} className="text-white/70 hover:text-white transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {bioLoading ? (
                <div className="flex flex-col items-center justify-center py-16 gap-4">
                  <div className="relative w-14 h-14">
                    <div className="absolute inset-0 rounded-full border-4 border-[#6B1A24]/20 animate-spin border-t-[#6B1A24]" />
                    <Wand2 className="absolute inset-0 m-auto w-6 h-6 text-[#6B1A24]" />
                  </div>
                  <p className="text-muted-foreground text-sm font-medium animate-pulse">Crafting your bio suggestions...</p>
                </div>
              ) : bioSuggestions.length > 0 ? (
                bioSuggestions.map((s, idx) => (
                  <div
                    key={idx}
                    onClick={() => setSelectedBioIdx(idx)}
                    className={`cursor-pointer rounded-xl border-2 p-4 transition-all duration-200 ${
                      selectedBioIdx === idx
                        ? "border-[#6B1A24] bg-[#FFF0F2] shadow-md"
                        : "border-border bg-card hover:border-[#6B1A24]/40 hover:bg-[#FFF0F2]/40"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${
                        selectedBioIdx === idx ? "bg-[#6B1A24] text-white" : "bg-muted text-muted-foreground"
                      }`}>
                        {s.label}
                      </span>
                      {selectedBioIdx === idx && (
                        <div className="w-5 h-5 rounded-full bg-[#6B1A24] flex items-center justify-center">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-foreground leading-relaxed">{s.text}</p>
                  </div>
                ))
              ) : (
                <p className="text-center text-muted-foreground py-8">No suggestions available.</p>
              )}
            </div>

            {/* Footer */}
            {!bioLoading && bioSuggestions.length > 0 && (
              <div className="px-6 py-4 border-t border-border flex items-center justify-between bg-card">
                <p className="text-xs text-muted-foreground">
                  {selectedBioIdx !== null ? "✓ Style selected — click Use This Bio to apply" : "Tap a suggestion to select it"}
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setBioModalOpen(false)} className="rounded-full">
                    Cancel
                  </Button>
                  <Button
                    disabled={selectedBioIdx === null}
                    onClick={() => {
                      if (selectedBioIdx !== null) {
                        updateField("about", bioSuggestions[selectedBioIdx].text);
                        setBioModalOpen(false);
                        toast.success("Bio applied! Feel free to edit it further.");
                      }
                    }}
                    className="rounded-full bg-[#6B1A24] hover:bg-[#9B2335] text-white gap-2"
                  >
                    <Check className="w-4 h-4" /> Use This Bio
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
}

function PF({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function PSel({ value, onChange, items, testid }) {
  return (
    <Select value={value || items[0]} onValueChange={onChange}>
      <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent>
        {items.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

// Searchable dropdown with "type your own value" support
function ProfileComboSelect({ value, onChange, options, placeholder, testid }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (open && inputRef.current) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  const filtered = options.filter((o) => o.toLowerCase().includes(query.toLowerCase()));

  const select = (val) => { onChange(val); setQuery(""); setOpen(false); };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") { setOpen(false); setQuery(""); }
    if (e.key === "Enter" && query.trim()) {
      filtered.length === 1 ? select(filtered[0]) : select(query.trim());
    }
  };

  return (
    <div ref={ref} className="relative" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`flex h-10 w-full items-center justify-between rounded-md border bg-background px-3 py-2 text-sm ring-offset-background transition-colors
          ${open ? "border-primary ring-2 ring-primary/20" : "border-input hover:border-primary/40"} focus:outline-none`}
        aria-expanded={open}
      >
        <span className={value ? "text-foreground" : "text-muted-foreground"}>{value || placeholder}</span>
        <svg className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${open ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-border bg-popover shadow-lg">
          <div className="flex items-center gap-2 border-b border-border px-3 py-2">
            <svg className="w-3.5 h-3.5 text-muted-foreground shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><circle cx="11" cy="11" r="8"/><path strokeLinecap="round" d="m21 21-4.35-4.35"/></svg>
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search or type custom value…"
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
            {query && <button type="button" onClick={() => setQuery("")} className="text-muted-foreground hover:text-foreground"><X className="w-3.5 h-3.5" /></button>}
          </div>
          <div className="max-h-56 overflow-y-auto py-1">
            {query.trim() && !options.some((o) => o.toLowerCase() === query.trim().toLowerCase()) && (
              <button type="button" onClick={() => select(query.trim())}
                className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-accent text-primary font-medium">
                <span className="text-xs bg-primary/10 text-primary rounded px-1.5 py-0.5 shrink-0">Custom</span>
                Use "{query.trim()}"
              </button>
            )}
            {filtered.map((opt) => (
              <button key={opt} type="button" onClick={() => select(opt)}
                className={`flex w-full items-center justify-between px-3 py-2 text-sm hover:bg-accent transition-colors ${value === opt ? "bg-accent/60 font-medium" : ""}`}>
                <span>{opt}</span>
                {value === opt && <Check className="w-3.5 h-3.5 text-primary" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


